import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Film, CheckCircle, AlertCircle, Loader2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VideoUpload({ onUploadComplete, onClose }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, processing, ready, error
  const [currentVideo, setCurrentVideo] = useState(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content_type: 'vod',
    category: 'match',
    competition_name: '',
    teams: '',
    is_premium: false
  });

  const getAuthConfig = () => {
    const token = localStorage.getItem('token');
    return {
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    };
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-matroska', 'video/webm'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid video file (MP4, MOV, MKV, WebM)');
      return;
    }

    // Validate file size (max 4GB)
    const maxSize = 4 * 1024 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 4GB.');
      return;
    }

    if (!formData.title) {
      // Auto-set title from filename
      const title = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
      setFormData(prev => ({ ...prev, title }));
    }

    await uploadVideo(file);
  };

  const uploadVideo = async (file) => {
    try {
      setUploading(true);
      setUploadStatus('uploading');
      setUploadProgress(0);

      // Step 1: Get upload URL from backend
      toast.info('Preparing upload...');
      const uploadUrlResponse = await axios.post(
        `${BACKEND_URL}/api/admin/videos/upload-url`,
        {
          title: formData.title || file.name,
          description: formData.description,
          content_type: formData.content_type,
          category: formData.category,
          competition_name: formData.competition_name,
          teams: formData.teams,
          is_premium: formData.is_premium,
          max_duration_seconds: 14400 // 4 hours
        },
        getAuthConfig()
      );

      const { upload_url, video_id, cloudflare_uid } = uploadUrlResponse.data;
      setCurrentVideo({ video_id, cloudflare_uid, title: formData.title });

      // Step 2: Upload file directly to Cloudflare
      toast.info('Uploading video to cloud...');
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      await axios.post(upload_url, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      setUploadStatus('processing');
      toast.success('Upload complete! Processing video...');

      // Step 3: Poll for processing status
      pollVideoStatus(video_id);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus('error');
      toast.error(error.response?.data?.detail || 'Failed to upload video');
      setUploading(false);
    }
  };

  const pollVideoStatus = async (videoId) => {
    let attempts = 0;
    const maxAttempts = 60; // 5 minutes max
    
    const checkStatus = async () => {
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/admin/videos/${videoId}/status`,
          getAuthConfig()
        );

        const { status, hls_url, thumbnail_url } = response.data;

        if (status === 'ready') {
          setUploadStatus('ready');
          setCurrentVideo(prev => ({ ...prev, hls_url, thumbnail_url }));
          toast.success('Video is ready! You can now publish it.');
          setUploading(false);
          return;
        }

        if (status === 'failed') {
          setUploadStatus('error');
          toast.error('Video processing failed. Please try again.');
          setUploading(false);
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 5000); // Check every 5 seconds
        } else {
          setUploadStatus('processing');
          toast.info('Processing is taking longer than expected. Check back later.');
          setUploading(false);
        }
      } catch (error) {
        console.error('Status check error:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 5000);
        }
      }
    };

    checkStatus();
  };

  const publishVideo = async () => {
    if (!currentVideo?.video_id) return;

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/videos/${currentVideo.video_id}/publish`,
        {},
        getAuthConfig()
      );

      toast.success('Video published successfully!');
      
      if (onUploadComplete) {
        onUploadComplete(response.data);
      }

      // Reset form
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish video');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      content_type: 'vod',
      category: 'match',
      competition_name: '',
      teams: '',
      is_premium: false
    });
    setUploadStatus('idle');
    setUploadProgress(0);
    setCurrentVideo(null);
    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="bg-[#2a2a2a] rounded-lg p-6 mb-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Film className="w-6 h-6 text-emerald-500" />
          Upload Video
        </h2>
        {onClose && (
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Upload Status Display */}
      {uploadStatus !== 'idle' && (
        <div className={`mb-6 p-4 rounded-lg ${
          uploadStatus === 'ready' ? 'bg-emerald-900/30 border border-emerald-600' :
          uploadStatus === 'error' ? 'bg-red-900/30 border border-red-600' :
          'bg-blue-900/30 border border-blue-600'
        }`}>
          <div className="flex items-center gap-3">
            {uploadStatus === 'uploading' && (
              <>
                <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
                <div className="flex-1">
                  <p className="text-white font-medium">Uploading... {uploadProgress}%</p>
                  <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              </>
            )}
            {uploadStatus === 'processing' && (
              <>
                <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
                <p className="text-white">Processing video... This may take a few minutes.</p>
              </>
            )}
            {uploadStatus === 'ready' && (
              <>
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                <div className="flex-1">
                  <p className="text-white font-medium">Video ready!</p>
                  <p className="text-gray-300 text-sm">{currentVideo?.title}</p>
                </div>
                <Button
                  onClick={publishVideo}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  Publish Now
                </Button>
              </>
            )}
            {uploadStatus === 'error' && (
              <>
                <AlertCircle className="w-5 h-5 text-red-400" />
                <p className="text-white">Upload failed. Please try again.</p>
                <Button onClick={resetForm} variant="outline" className="text-white border-red-600">
                  Retry
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Upload Form */}
      {uploadStatus === 'idle' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-300">Title *</Label>
              <Input
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Match or video title"
                className="mt-1 bg-[#1a1a1a] border-gray-600 text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300">Teams/Match</Label>
              <Input
                name="teams"
                value={formData.teams}
                onChange={handleInputChange}
                placeholder="e.g., Nigeria vs Egypt"
                className="mt-1 bg-[#1a1a1a] border-gray-600 text-white"
              />
            </div>
          </div>

          <div>
            <Label className="text-gray-300">Description</Label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={2}
              placeholder="Brief description of the video"
              className="w-full mt-1 px-3 py-2 bg-[#1a1a1a] border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-gray-300">Content Type</Label>
              <select
                name="content_type"
                value={formData.content_type}
                onChange={handleInputChange}
                className="w-full mt-1 px-3 py-2 bg-[#1a1a1a] border border-gray-600 rounded-lg text-white"
              >
                <option value="vod">Full Match (VOD)</option>
                <option value="highlight">Highlight</option>
                <option value="original">Original Content</option>
              </select>
            </div>
            <div>
              <Label className="text-gray-300">Category</Label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full mt-1 px-3 py-2 bg-[#1a1a1a] border border-gray-600 rounded-lg text-white"
              >
                <option value="match">Match</option>
                <option value="highlight">Highlight</option>
                <option value="documentary">Documentary</option>
                <option value="original">Original</option>
              </select>
            </div>
            <div>
              <Label className="text-gray-300">Competition</Label>
              <select
                name="competition_name"
                value={formData.competition_name}
                onChange={handleInputChange}
                className="w-full mt-1 px-3 py-2 bg-[#1a1a1a] border border-gray-600 rounded-lg text-white"
              >
                <option value="">Select competition</option>
                <option value="AFCON 2025">AFCON 2025</option>
                <option value="CAF Champions League">CAF Champions League</option>
                <option value="CHAN">CHAN</option>
                <option value="WAFCON">WAFCON</option>
                <option value="Nigeria Professional League">Nigeria Professional League</option>
                <option value="PSL">PSL (South Africa)</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-white cursor-pointer">
              <input
                type="checkbox"
                name="is_premium"
                checked={formData.is_premium}
                onChange={handleInputChange}
                className="w-4 h-4 text-emerald-600 bg-[#1a1a1a] border-gray-600 rounded"
              />
              <span>Premium Content (requires subscription)</span>
            </label>
          </div>

          {/* File Upload Area */}
          <div 
            className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center hover:border-emerald-500 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-white font-medium mb-2">Click to select video file</p>
            <p className="text-gray-400 text-sm">MP4, MOV, MKV, WebM • Max 4GB • Up to 4 hours</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/quicktime,video/x-matroska,video/webm"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </div>
      )}
    </div>
  );
}
